import { Laya } from "../../../../Laya";
import { LayaEnv } from "../../../../LayaEnv";
import { Color } from "../../../maths/Color";
import { Vector4 } from "../../../maths/Vector4";
import { BufferTargetType } from "../../../RenderEngine/RenderEnum/BufferTargetType";
import { RenderCapable } from "../../../RenderEngine/RenderEnum/RenderCapable";
import { RenderClearFlag } from "../../../RenderEngine/RenderEnum/RenderClearFlag";
import { GL2TextureContext } from "./GL2TextureContext";
import { GLTextureContext } from "./GLTextureContext";
import { GLBuffer } from "./WebGLEngine/GLBuffer";
import { WebGLExtension } from "./WebGLEngine/GLEnum/WebGLExtension";
import { WebGLMode } from "./WebGLEngine/GLEnum/WebGLMode";
import { GLParams } from "./WebGLEngine/GLParams";
import { GLRenderDrawContext } from "./WebGLEngine/GLRenderDrawContext";
import { GLRenderState } from "./WebGLEngine/GLRenderState";
import { GLShaderInstance } from "./WebGLEngine/GLShaderInstance";
import { GLVertexState } from "./WebGLEngine/GLVertexState";
import { GlCapable } from "./WebGLEngine/GlCapable";
import { ShaderDefine } from "../../RenderModuleData/Design/ShaderDefine";
import { EventDispatcher } from "../../../events/EventDispatcher";
import { RenderTargetFormat } from "../../../RenderEngine/RenderEnum/RenderTargetFormat";
import { WebGLUniformBufferManager } from "./WebGLUniformBufferManager";
import { Config } from "../../../../Config";
export class WebGLEngine extends EventDispatcher {
    get lost() {
        return this._lost;
    }
    constructor(config, webglMode = WebGLMode.Auto) {
        super();
        this._framePassCount = 0;
        this._lost = false;
        this._propertyNameMap = {};
        this._propertyNameCounter = 0;
        this._IDCounter = 0;
        this._isShaderDebugMode = true;
        this._lastClearColor = new Color;
        this._lastClearDepth = -1;
        this._remapZ = true;
        this._screenInvertY = false;
        this._lodTextureSample = true;
        this._breakTextureSample = true;
        this._config = config;
        this._isWebGL2 = false;
        this._lastViewport = new Vector4(0, 0, 0, 0);
        this._lastClearColor = new Color(0, 0, 0, 0);
        this._lastScissor = new Vector4(0, 0, 0, 0);
        this._webglMode = webglMode;
        WebGLEngine.instance = this;
    }
    startFrame() {
        this.event("startFrame", null);
    }
    endFrame() {
        this.event("endFrame", null);
        this._framePassCount = 0;
    }
    getInnerWidth() {
        if (LayaEnv.isConch) {
            return window.getInnerWidth();
        }
        else
            return this._globalWidth;
    }
    getInnerHeight() {
        if (LayaEnv.isConch) {
            return window.getInnerHeight();
        }
        else
            return this._globalHeight;
    }
    resizeOffScreen(width, height) {
        this._globalWidth = width;
        this._globalHeight = height;
        if (LayaEnv.isConch) {
            if (WebGLEngine._lastFrameBuffer) {
                WebGLEngine._lastFrameBuffer.dispose();
                WebGLEngine._lastFrameBuffer_WebGLOBJ = null;
            }
            WebGLEngine._lastFrameBuffer = this.getTextureContext().createRenderTargetInternal(width, height, RenderTargetFormat.R8G8B8A8, RenderTargetFormat.None, false, false, 1, false);
            WebGLEngine._lastFrameBuffer_WebGLOBJ = WebGLEngine._lastFrameBuffer._framebuffer;
        }
    }
    addTexGammaDefine(key, value) {
        WebGLEngine._texGammaDefine[key] = value;
    }
    get gl() {
        return this._context;
    }
    get isWebGL2() {
        return this._isWebGL2;
    }
    get webglConfig() {
        return this._config;
    }
    initRenderEngine(canvas) {
        let names;
        let gl;
        switch (this._webglMode) {
            case WebGLMode.Auto:
                names = ["webgl2", "experimental-webgl2", "webgl", "experimental-webgl"];
                break;
            case WebGLMode.WebGL1:
                names = ["webgl", "experimental-webgl"];
                break;
            case WebGLMode.WebGL2:
                names = ["webgl2", "experimental-webgl2"];
                break;
        }
        for (let i = 0; i < names.length; i++) {
            try {
                gl = canvas.getContext(names[i], this._config);
            }
            catch (e) {
            }
            if (gl) {
                if (names[i] === 'webgl2' || names[i] === 'experimental-webgl2') {
                    this._isWebGL2 = true;
                }
                break;
            }
        }
        this._context = gl;
        this.scissorTest(true);
        this._initBindBufferMap();
        this._supportCapatable = new GlCapable(this);
        this._GLParams = new GLParams(this);
        this._GLRenderState = new GLRenderState(this);
        this._glTextureIDParams = [gl.TEXTURE0, gl.TEXTURE1, gl.TEXTURE2, gl.TEXTURE3, gl.TEXTURE4, gl.TEXTURE5, gl.TEXTURE6, gl.TEXTURE7, gl.TEXTURE8, gl.TEXTURE9, gl.TEXTURE10, gl.TEXTURE11, gl.TEXTURE12, gl.TEXTURE13, gl.TEXTURE14, gl.TEXTURE15, gl.TEXTURE16, gl.TEXTURE17, gl.TEXTURE18, gl.TEXTURE19, gl.TEXTURE20, gl.TEXTURE21, gl.TEXTURE22, gl.TEXTURE23, gl.TEXTURE24, gl.TEXTURE25, gl.TEXTURE26, gl.TEXTURE27, gl.TEXTURE28, gl.TEXTURE29, gl.TEXTURE30, gl.TEXTURE31];
        this._activedTextureID = gl.TEXTURE0;
        this._activeTextures = [];
        this._GLTextureContext = this.isWebGL2 ? new GL2TextureContext(this) : new GLTextureContext(this);
        this._GLRenderDrawContext = new GLRenderDrawContext(this);
        canvas.addEventListener("webglcontextlost", this.webglContextLost);
        Config._uniformBlock = Config.enableUniformBufferObject && this.getCapable(RenderCapable.UnifromBufferObject);
        Config.matUseUBO = Config.matUseUBO && this.getCapable(RenderCapable.UnifromBufferObject);
        this._initBufferBlock();
    }
    _initBufferBlock() {
        const useUBO = (Config._uniformBlock || Config.matUseUBO);
        if (useUBO) {
            const gl = this._context;
            let offsetAlignment = gl.getParameter(gl.UNIFORM_BUFFER_OFFSET_ALIGNMENT);
            this.bufferMgr = new WebGLUniformBufferManager(this, offsetAlignment);
            let maxBlockCount = gl.getParameter(gl.MAX_UNIFORM_BUFFER_BINDINGS);
            this._uboBindingMap = new Array(maxBlockCount);
            for (let i = 0; i < maxBlockCount; i++) {
                this._uboBindingMap[i] = { buffer: null, offset: 0, size: 0 };
            }
        }
    }
    webglContextLost(e) {
        console.log("lost webgl context");
        Laya.stage.event("GraphicContextLost", e);
        this._lost = true;
    }
    _initBindBufferMap() {
        this._GLBufferBindMap = {};
        this._GLBufferBindMap[BufferTargetType.ARRAY_BUFFER] = null;
        this._GLBufferBindMap[BufferTargetType.ELEMENT_ARRAY_BUFFER] = null;
        this._GLBufferBindMap[BufferTargetType.UNIFORM_BUFFER] = null;
    }
    _getbindBuffer(target) {
        return this._GLBufferBindMap[target];
    }
    _setbindBuffer(target, buffer) {
        this._GLBufferBindMap[target] = buffer;
    }
    _bindTexture(target, texture) {
        const texID = this._activedTextureID - this._context.TEXTURE0;
        if (this._activeTextures[texID] !== texture) {
            this._context.bindTexture(target, texture);
            this._activeTextures[texID] = texture;
        }
    }
    getCapable(capatableType) {
        return this._supportCapatable.getCapable(capatableType);
    }
    viewport(x, y, width, height) {
        const gl = this._context;
        const lv = this._lastViewport;
        if (LayaEnv.isConch) {
            gl.viewport(x, y, width, height);
        }
        else if (x !== lv.x || y !== lv.y || width !== lv.z || height !== lv.w) {
            gl.viewport(x, y, width, height);
            lv.setValue(x, y, width, height);
        }
    }
    scissor(x, y, width, height) {
        const gl = this._context;
        const lv = this._lastScissor;
        if (LayaEnv.isConch) {
            gl.scissor(x, y, width, height);
        }
        else if (x !== lv.x || y !== lv.y || width !== lv.z || height !== lv.w) {
            gl.scissor(x, y, width, height);
            lv.setValue(x, y, width, height);
        }
    }
    scissorTest(value) {
        if (this._scissorState == value)
            return;
        this._scissorState = value;
        if (value)
            this._context.enable(this._context.SCISSOR_TEST);
        else
            this._context.disable(this._context.SCISSOR_TEST);
    }
    clearRenderTexture(clearFlag, clearcolor = null, clearDepth = 1, clearStencilValue = 0) {
        var flag;
        if (clearFlag & RenderClearFlag.Color) {
            if (clearcolor && !this._lastClearColor.equal(clearcolor)) {
                this._context.clearColor(clearcolor.r, clearcolor.g, clearcolor.b, clearcolor.a);
                clearcolor.cloneTo(this._lastClearColor);
            }
            flag |= this.gl.COLOR_BUFFER_BIT;
        }
        if (clearFlag & RenderClearFlag.Depth) {
            if (this._lastClearDepth != clearDepth) {
                this._context.clearDepth(clearDepth);
                this._lastClearDepth = clearDepth;
            }
            this._GLRenderState.setDepthMask(true);
            flag |= this._context.DEPTH_BUFFER_BIT;
        }
        if (clearFlag & RenderClearFlag.Stencil) {
            this._context.clearStencil(clearStencilValue);
            this._GLRenderState.setStencilWrite(true);
            flag |= this._context.STENCIL_BUFFER_BIT;
        }
        if (flag)
            this._context.clear(flag);
    }
    copySubFrameBuffertoTex(texture, level, xoffset, yoffset, x, y, width, height) {
        this._bindTexture(texture.target, texture.resource);
        this._context.copyTexSubImage2D(texture.target, level, xoffset, yoffset, x, y, width, height);
    }
    colorMask(r, g, b, a) {
        this._context.colorMask(r, g, b, a);
    }
    getParams(params) {
        return this._GLParams.getParams(params);
    }
    createBuffer(targetType, bufferUsageType) {
        return new GLBuffer(this, targetType, bufferUsageType);
    }
    createShaderInstance(vs, ps, attributeMap) {
        return new GLShaderInstance(this, vs, ps, attributeMap);
    }
    createVertexState() {
        return new GLVertexState(this);
    }
    getTextureContext() {
        return this._GLTextureContext;
    }
    getDrawContext() {
        return this._GLRenderDrawContext;
    }
    propertyNameToID(name) {
        if (this._propertyNameMap[name] != null) {
            return this._propertyNameMap[name];
        }
        else {
            var id = this._propertyNameCounter++;
            this._propertyNameMap[name] = id;
            this._propertyNameMap[id] = name;
            return id;
        }
    }
    propertyIDToName(id) {
        return this._propertyNameMap[id];
    }
    getNamesByDefineData(defineData, out) {
        var maskMap = WebGLEngine._maskMap;
        var mask = defineData._mask;
        out.length = 0;
        for (var i = 0, n = defineData._length; i < n; i++) {
            var subMaskMap = maskMap[i];
            var subMask = mask[i];
            for (var j = 0; j < 32; j++) {
                var d = 1 << j;
                if (subMask > 0 && d > subMask)
                    break;
                if (subMask & d)
                    out.push(subMaskMap[d]);
            }
        }
    }
    getDefineByName(name) {
        var define = WebGLEngine._defineMap[name];
        if (!define) {
            var maskMap = WebGLEngine._maskMap;
            var counter = WebGLEngine._defineCounter;
            var index = Math.floor(counter / 32);
            var value = 1 << counter % 32;
            define = new ShaderDefine(index, value);
            WebGLEngine._defineMap[name] = define;
            if (index == maskMap.length) {
                maskMap.length++;
                maskMap[index] = {};
            }
            maskMap[index][value] = name;
            WebGLEngine._defineCounter++;
        }
        return define;
    }
    uploadUniforms(shader, commandEncoder, shaderData, uploadUnTexture) {
        var data = shaderData._data;
        var shaderUniform = commandEncoder.getArrayData();
        var shaderCall = 0;
        for (var i = 0, n = shaderUniform.length; i < n; i++) {
            var one = shaderUniform[i];
            if (uploadUnTexture || one.textureID !== -1) {
                var value = data[one.dataOffset];
                if (value != null)
                    shaderCall += one.fun.call(one.caller, one, value);
            }
        }
        return shaderCall;
    }
    uploadOneUniforms(shader, shaderVariable, data) {
        shader.bind();
        if (shaderVariable && data != null)
            shaderVariable.fun.call(shaderVariable.caller, shaderVariable, data);
    }
    unbindVertexState() {
        if (this.isWebGL2)
            this._context.bindVertexArray(null);
        else
            this._supportCapatable.getExtension(WebGLExtension.OES_vertex_array_object).bindVertexArrayOES(null);
        this._GLBindVertexArray = null;
    }
}
WebGLEngine._texGammaDefine = {};
WebGLEngine._lastFrameBuffer = null;
WebGLEngine._lastFrameBuffer_WebGLOBJ = null;
WebGLEngine._defineMap = {};
WebGLEngine._defineCounter = 0;
WebGLEngine._maskMap = [];
